import type { JSX, ReactNode } from 'react';
import { Icon } from '@ndrc/ui';
import type { ClassDocument } from '../lib/queries';
import { formatParisDate, formatParisTime } from '../lib/clock';
import { t } from '../messages';
import { formatFileSize } from './fileSize';
import './documents.css';

/**
 * **La liste des documents, écrite UNE fois, lue par deux écrans.**
 *
 * L'élève l'ouvre depuis son dock (« Documents ») ; l'enseignant la voit dans son espace, avec un
 * bouton « Retirer » de plus. Ce sont les mêmes lignes, la même mise en page, les mêmes mots — deux
 * rédactions auraient divergé au premier changement, et l'enseignant a besoin de voir exactement ce
 * que sa classe voit.
 *
 * **Le lien ouvre dans un nouvel onglet, et c'est un vrai lien.** Pas un bouton qui appellerait
 * `fetch` : le navigateur sait déjà afficher un PDF ou une image, et il envoie le cookie de session
 * de lui-même sur une navigation vers la même origine — ce que le pipeline RPC, lui, ne peut pas
 * faire pour une balise. `rel="noreferrer"` par principe sur toute cible `_blank`.
 *
 * **`url === null` n'affiche pas de lien.** Le cœur ne fabrique une adresse signée que pour une
 * vraie session (voir `ClassDocument.url`). Ce cas ne se produit pas dans un navigateur ; s'il se
 * produisait, l'écran montrerait le document sans prétendre pouvoir l'ouvrir, plutôt qu'un lien
 * mort.
 */

/** La date de dépôt, à Paris — `null` si l'horodatage est illisible, auquel cas on n'affiche rien
 * plutôt qu'une date inventée. */
function depositedAt(iso: string): string | null {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const at = new Date(ms);
  return t('documents.deposited', { date: formatParisDate(at), time: formatParisTime(at) });
}

export interface DocumentListProps {
  documents: readonly ClassDocument[];
  /** Rendu à droite de chaque ligne — le bouton « Retirer » de l'enseignant, rien chez l'élève. */
  action?: (document: ClassDocument) => ReactNode;
}

export function DocumentList({ documents, action }: DocumentListProps): JSX.Element {
  return (
    <ul className="documents-list">
      {documents.map((document) => {
        const when = depositedAt(document.createdAt);
        return (
          <li className="documents-item" key={document.id}>
            <span className="documents-glyph" aria-hidden="true">
              <Icon name="file" size={17} strokeWidth={1.7} />
            </span>
            <span className="documents-meta">
              <span className="documents-name">{document.name}</span>
              <span className="documents-sub">
                {formatFileSize(document.sizeBytes)}
                {when === null ? null : <span className="documents-when">{when}</span>}
              </span>
            </span>
            <span className="documents-actions">
              {/* Pas d'attribut `download` sur le lien ci-dessous : c'est le SERVEUR qui décide si
                  un document s'ouvre dans l'onglet (PDF, image, texte) ou se télécharge (Word,
                  tableur), par son en-tête `Content-Disposition` — voir `@ndrc/core-files`,
                  `accepted-types.ts`. Forcer le téléchargement ici obligerait l'élève à ouvrir sa
                  consigne depuis son dossier de téléchargements. */}
              {document.url === null ? (
                <span className="documents-unavailable">{t('documents.noLink')}</span>
              ) : (
                <a className="ndrc-button" href={document.url} target="_blank" rel="noreferrer">
                  {t('documents.open')}
                </a>
              )}
              {action?.(document)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
